/**
 * Phase 17 — Goods Receipt & Delivery Pure Calculation Engine
 * Deterministic, pure functions for quantity reconciliation, delay detection,
 * quality rejection rates, and 3-way matching validation.
 */

import {
  DeliveryStatus,
  QualityInspectionStatus,
  VendorDeliveryPerformance,
  GoodsReceiptRecord,
  DeliveryTrackingRecord,
} from '@/types/domain/grn-management';

/**
 * Calculates net accepted quantity from gross physical received and rejected quantities.
 * Prevents negative values.
 */
export function calculateAcceptedQuantity(receivedQuantity: number, rejectedQuantity: number): number {
  const safeReceived = Math.max(0, receivedQuantity);
  const safeRejected = Math.max(0, rejectedQuantity);
  return Math.max(0, safeReceived - safeRejected);
}

/**
 * Calculates remaining pending balance against the purchase order.
 * Pending = max(0, Ordered - Accepted)
 */
export function calculatePendingQuantity(orderedQuantity: number, acceptedQuantity: number): number {
  const safeOrdered = Math.max(0, orderedQuantity);
  const safeAccepted = Math.max(0, acceptedQuantity);
  return Math.max(0, safeOrdered - safeAccepted);
}

/**
 * Calculates quality rejection rate as a percentage rounded to 2 decimal places.
 */
export function calculateRejectionRate(totalReceived: number, totalRejected: number): number {
  if (totalReceived <= 0) return 0;
  const rate = (totalRejected / totalReceived) * 100;
  return Number(Math.min(100, Math.max(0, rate)).toFixed(2));
}

/**
 * Detects delivery overdue delay against promised target date.
 */
export function detectDeliveryDelay(
  promisedDeliveryDate: string,
  actualReceiptDate?: string,
  isFullyDelivered: boolean = false
): { isDelayed: boolean; delayDays: number } {
  if (!promisedDeliveryDate) {
    return { isDelayed: false, delayDays: 0 };
  }

  const target = new Date(promisedDeliveryDate).getTime();
  const comparison = actualReceiptDate && isFullyDelivered ? new Date(actualReceiptDate).getTime() : Date.now();

  const diffMs = comparison - target;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    return { isDelayed: true, delayDays: diffDays };
  }

  return { isDelayed: false, delayDays: 0 };
}

/**
 * Derives comprehensive delivery fulfillment status based on quantities, target dates, and cancellation states.
 */
export function deriveDeliveryStatus(params: {
  orderedQuantity: number;
  receivedQuantity: number;
  acceptedQuantity: number;
  rejectedQuantity: number;
  promisedDeliveryDate: string;
  actualReceiptDate?: string;
  isCancelled?: boolean;
  qualityHold?: boolean;
}): DeliveryStatus {
  const {
    orderedQuantity,
    receivedQuantity,
    acceptedQuantity,
    rejectedQuantity,
    promisedDeliveryDate,
    actualReceiptDate,
    isCancelled = false,
    qualityHold = false,
  } = params;

  if (isCancelled) return 'CANCELLED';
  if (qualityHold) return 'QUALITY_HOLD';

  if (orderedQuantity > 0 && acceptedQuantity >= orderedQuantity) {
    return 'DELIVERED';
  }

  if (receivedQuantity > 0 && acceptedQuantity === 0 && rejectedQuantity > 0) {
    return 'PARTIALLY_REJECTED';
  }

  if (acceptedQuantity > 0 && acceptedQuantity < orderedQuantity) {
    return 'PARTIALLY_DELIVERED';
  }

  // Not yet accepted or received
  const { isDelayed } = detectDeliveryDelay(promisedDeliveryDate, actualReceiptDate, false);
  if (isDelayed) return 'OVERDUE';

  const targetDate = new Date(promisedDeliveryDate);
  const now = new Date();
  const isToday =
    targetDate.getFullYear() === now.getFullYear() &&
    targetDate.getMonth() === now.getMonth() &&
    targetDate.getDate() === now.getDate();

  if (isToday) return 'DUE_TODAY';

  const daysToTarget = Math.ceil((targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (daysToTarget <= 3 && daysToTarget > 0) return 'DUE_SOON';

  return 'NOT_DUE';
}

/**
 * Derives overall quality status for a Goods Receipt from line item rejection counts.
 */
export function deriveOverallQualityStatus(
  totalReceived: number,
  totalAccepted: number,
  totalRejected: number,
  isOnHold: boolean = false
): QualityInspectionStatus {
  if (isOnHold) return 'ON_HOLD';
  if (totalReceived === 0) return 'PENDING_INSPECTION';
  if (totalRejected === 0 && totalAccepted > 0) return 'PASSED';
  if (totalAccepted === 0 && totalRejected > 0) return 'FAILED';
  if (totalAccepted > 0 && totalRejected > 0) return 'PARTIALLY_PASSED';
  return 'PENDING_INSPECTION';
}

/**
 * Tolerance-aware 3-Way Match Validation (PO vs GRN vs Invoice).
 */
export function calculateThreeWayMatch(
  poQuantity: number,
  poUnitPrice: number,
  grnAcceptedQuantity: number,
  invoiceQuantity?: number,
  invoiceUnitPrice?: number
): 'MATCHED' | 'QUANTITY_MISMATCH' | 'PRICE_MISMATCH' | 'INVOICE_PENDING' {
  if (invoiceQuantity === undefined || invoiceUnitPrice === undefined) {
    return 'INVOICE_PENDING';
  }

  if (poQuantity !== grnAcceptedQuantity || grnAcceptedQuantity !== invoiceQuantity) {
    return 'QUANTITY_MISMATCH';
  }

  const priceDiff = Math.abs(poUnitPrice - invoiceUnitPrice);
  if (priceDiff > 0.01) {
    return 'PRICE_MISMATCH';
  }

  return 'MATCHED';
}

/**
 * Computes vendor delivery scorecard metrics across multiple GRNs and deliveries.
 */
export function calculateVendorPerformance(
  vendorId: string,
  vendorName: string,
  vendorCode: string,
  grns: GoodsReceiptRecord[],
  deliveries: DeliveryTrackingRecord[]
): VendorDeliveryPerformance {
  const vendorDeliveries = deliveries.filter((d) => d.vendorId === vendorId || d.vendorCode === vendorCode);
  const vendorGrns = grns.filter((g) => g.vendorId === vendorId || g.vendorCode === vendorCode);

  const totalDeliveries = vendorDeliveries.length;
  const lateDeliveries = vendorDeliveries.filter((d) => d.isDelayed).length;
  const onTimeDeliveries = Math.max(0, totalDeliveries - lateDeliveries);
  const onTimeDeliveryRate = totalDeliveries > 0 ? Number(((onTimeDeliveries / totalDeliveries) * 100).toFixed(1)) : 100;

  const totalDelayDays = vendorDeliveries.reduce((sum, d) => sum + (d.delayDays || 0), 0);
  const averageDelayDays = lateDeliveries > 0 ? Number((totalDelayDays / lateDeliveries).toFixed(1)) : 0;

  const totalReceivedUnits = vendorGrns.reduce((sum, g) => sum + g.totalReceivedQuantity, 0);
  const totalAcceptedUnits = vendorGrns.reduce((sum, g) => sum + g.totalAcceptedQuantity, 0);
  const totalRejectedUnits = vendorGrns.reduce((sum, g) => sum + g.totalRejectedQuantity, 0);

  const qualityAcceptanceRate = totalReceivedUnits > 0
    ? Number(((totalAcceptedUnits / totalReceivedUnits) * 100).toFixed(1))
    : 100;

  const rejectionRate = totalReceivedUnits > 0
    ? Number(((totalRejectedUnits / totalReceivedUnits) * 100).toFixed(1))
    : 0;

  return {
    vendorId,
    vendorName,
    vendorCode,
    totalDeliveries,
    onTimeDeliveries,
    lateDeliveries,
    onTimeDeliveryRate,
    averageDelayDays,
    totalReceivedUnits,
    totalAcceptedUnits,
    totalRejectedUnits,
    qualityAcceptanceRate,
    rejectionRate,
  };
}
